import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { PageHeader } from '@/components/shared/page-header';
import { AccountsTable } from '@/modules/accounts/components/accounts-table';
import { useDebounce } from '@/hooks/use-debounce';
import {
  useAccounts,
  useAccountsFilters,
  useAccountsPagination
} from '@/hooks/use-accounts';
import { ACCOUNT_STATUSES } from '@/constants/feature-labels';

export default function AccountsPage() {
  const { data, isLoading, isFetching, error } = useAccounts();
  const [filters, setFilters] = useAccountsFilters();
  const [{ page, pageSize }, setPagination] = useAccountsPagination();
  const [searchInput, setSearchInput] = useState(filters.search);

  const debouncedSearch = useDebounce(searchInput);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ ...filters, search: debouncedSearch });
      setPagination({ page: 1, pageSize });
    }
  }, [debouncedSearch, filters, setFilters, setPagination, pageSize]);

  const accounts = data?.accounts ?? [];
  const totalPages = data?.pagination?.totalPages ?? 1;

  return (
    <div className='mx-auto w-full max-w-6xl space-y-6 p-6 lg:p-8'>
      <PageHeader
        title='Accounts'
        description='Manage client accounts, users, features and products.'
      />

      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative w-full sm:w-[300px]'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by name or ID…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className='pl-9'
          />
        </div>
        <Select
          value={filters.source}
          onValueChange={(v) => {
            setFilters({ ...filters, source: v as typeof filters.source });
            setPagination({ page: 1, pageSize });
          }}
        >
          <SelectTrigger className='w-[190px]'>
            <SelectValue placeholder='Where it lives' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All accounts</SelectItem>
            <SelectItem value='both'>In both systems</SelectItem>
            <SelectItem value='postgres'>Main system only</SelectItem>
            <SelectItem value='mongo'>Legacy only</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.account_status}
          onValueChange={(v) => {
            setFilters({ ...filters, account_status: v });
            setPagination({ page: 1, pageSize });
          }}
        >
          <SelectTrigger className='w-[170px]'>
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            {ACCOUNT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? (
        <div className='rounded-xl border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive'>
          {error instanceof Error ? error.message : 'Failed to load accounts.'}
        </div>
      ) : (
        <AccountsTable
          accounts={accounts}
          isLoading={isLoading || isFetching}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPagination({ page: p, pageSize })}
        />
      )}
    </div>
  );
}
